/**
 * gamba-landing-suscripcion.js — hidrata los números de la app de suscripciones
 * en la landing `/pages/suscripcion`.
 *
 * QUÉ PROBLEMA RESUELVE, en una línea: **nueve números de esa pantalla no son
 * diseño, son configuración.** `docs/integracion-suscripciones.md` es explícito
 * en que el porcentaje de descuento, el umbral de envío gratis y las
 * frecuencias los edita Gamba en el backoffice y la tienda los tiene que
 * reflejar **sin deploy**. La sección 13 de
 * `docs/mediciones/fase-4-a-landing-suscripcion.md` los lista uno por uno. El
 * que los escriba a mano rompe esa promesa, y el síntoma es el peor de todos:
 * una landing que promete 10% cuando la app cobra 15%.
 *
 * NO LEE EL ENDPOINT: eso lo hace `gamba-subscription-config.js`, que es el
 * único lugar del theme donde viven la URL, los fallbacks y la caché. Acá sólo
 * se pinta. La sección emite los dos tags en orden (config primero, éste
 * después) y este módulo entra por `window.GambaSubscriptionConfig?.load()`.
 * El `?.` no es adorno: si alguien se olvida el tag del config, la landing se
 * queda con lo que pintó Liquid en vez de tirar un TypeError.
 *
 * ---------------------------------------------------------------------------
 * EL CONTRATO CON EL MARKUP — tres ganchos, y ninguno es obligatorio.
 *
 *   [data-gamba-sus-subtitle-tpl]   plantilla de un texto suelto (la bajada del
 *                                   hero). El nodo que la lleva es el que se
 *                                   reescribe.
 *   [data-gamba-sus-title-tpl]      plantilla del título de una card de
 *   [data-gamba-sus-text-tpl]       beneficio. Van en la RAÍZ de la card y el
 *                                   destino es su `.gamba-benefit-card__title`
 *                                   / `__text`, porque el snippet compartido
 *                                   sólo expone `attrs` en la raíz. Si algún
 *                                   día la card cambia esos nombres de clase,
 *                                   esto deja de encontrar el nodo — está
 *                                   anotado también en el docblock del snippet.
 *   [data-gamba-sus-frequency]      el `count` de meses de una card de
 *                                   frecuencia. Se esconde si el endpoint no la
 *                                   tiene habilitada.
 *
 * Los centinelas son `__PERCENT__` y `__AMOUNT__`, la misma forma que ya usa el
 * bloque lima de la PDP. Van **sin llaves** a propósito: una llave que cierra
 * adentro de una salida `{{ … }}` de Liquid corta el tokenizador y Shopify
 * rechaza el archivo entero, en silencio. Pasó de verdad en este proyecto.
 *
 * EL SEGUNDO ERA `__UNITS__` Y LLEVABA UN NÚMERO PELADO. Con el cambio del
 * 2026-09-16 el umbral de envío gratis dejó de contar unidades y pasó a ser un
 * MONTO en centavos (el porqué, entero, en `gamba-subscription-config.js`), así
 * que este módulo dejó de ser un reemplazador de strings puro: ahora formatea
 * plata, y para eso necesita el formato de moneda y la divisa que la sección
 * emite en `data-gamba-money-format` / `data-gamba-currency`. Sin ese par no
 * hay forma de escribir el monto bien, así que la plantilla **se deja como la
 * pintó Liquid** en vez de meter un `4500000` en la pantalla.
 *
 * POR QUÉ `innerHTML` Y NO `textContent`: el título de la card puede traer un
 * `<br>` deliberado (el beneficio 4 del boceto parte en dos líneas). La
 * plantilla llega **ya escapada desde Liquid** —`escape` primero,
 * `newline_to_br` después, y `escape` otra vez para meterla en el atributo—,
 * así que lo único que sobrevive como markup son los `<br>` que generó el
 * filtro. Un texto de setting con `<script>` sale como texto literal. **Si
 * alguien cambia ese orden de filtros en la sección, esto pasa a ser un XSS.**
 *
 * IDEMPOTENTE Y SIN ESTADO: se puede correr dos veces (lo hace: una síncrona
 * con `read()` y otra cuando resuelve `load()`) porque siempre reescribe desde
 * la plantilla original, que nunca se toca. Por eso la plantilla vive en el
 * atributo y no en el DOM ya pintado: releer el texto pintado y volver a
 * reemplazar daría un número sobre otro.
 * ---------------------------------------------------------------------------
 */

import { formatMoney } from '@theme/money-formatting';

const PERCENT_TOKEN = '__PERCENT__';
const AMOUNT_TOKEN = '__AMOUNT__';

/**
 * El formato de moneda de la tienda, leído UNA vez del DOM.
 *
 * Vive en el bloque de beneficios (`sections/gamba-benefit-cards.liquid`) y se
 * busca en todo el documento a propósito: el centinela del monto puede aparecer
 * en el copy de cualquier sección de la landing, y el par de moneda es el mismo
 * para toda la página. Si no está —una landing sin el bloque de beneficios—,
 * devuelve `null` y el monto no se reemplaza.
 *
 * @returns {{ format: string, currency: string } | null}
 */
function moneyContext() {
  const node = document.querySelector('[data-gamba-money-format][data-gamba-currency]');
  if (!(node instanceof HTMLElement)) return null;

  const format = node.dataset.gambaMoneyFormat;
  const currency = node.dataset.gambaCurrency;
  if (!format || !currency) return null;

  return { format, currency };
}

/**
 * Reemplaza los dos centinelas de una plantilla.
 *
 * `split`/`join` y no una regex con `g`: el centinela es un literal y así no hay
 * que escapar nada ni construir una regex por llamada.
 *
 * EL MONTO SÓLO SE REEMPLAZA SI HAY CON QUÉ FORMATEARLO. Sin el par de moneda,
 * o con un umbral 0 —que es como la app APAGA el envío gratis—, el centinela
 * queda sin tocar y el nodo se queda con lo que pintó Liquid. Es feo en el
 * primer caso y correcto en los dos: lo que no puede pasar es que la landing
 * anuncie "desde 4500000 por pedido", o que prometa envío gratis desde $0.
 *
 * @param {string} template
 * @param {{ discountPercent: number, freeShippingAmountCents: number }} config
 * @param {{ format: string, currency: string } | null} money
 * @returns {string}
 */
function fill(template, config, money) {
  const filled = template.split(PERCENT_TOKEN).join(String(config.discountPercent));

  const cents = Number(config.freeShippingAmountCents);
  if (!money || !Number.isFinite(cents) || cents <= 0) return filled;

  return filled.split(AMOUNT_TOKEN).join(formatMoney(cents, money.format, money.currency));
}

/**
 * Escribe una plantilla en un nodo, si el nodo existe y la plantilla trae algo.
 *
 * @param {Element | null | undefined} node
 * @param {string | undefined} template
 * @param {{ discountPercent: number, freeShippingAmountCents: number }} config
 * @param {{ format: string, currency: string } | null} money
 */
function paint(node, template, config, money) {
  if (!node || !template) return;

  const next = fill(template, config, money);
  // Comparar antes de escribir evita un reflow y —lo que importa más— evita
  // pisar el DOM en la pasada síncrona cuando el valor ya es el correcto, que
  // es el caso normal: el fallback de Liquid y el de JS son el mismo número.
  if (node.innerHTML !== next) node.innerHTML = next;
}

/**
 * @param {ReturnType<typeof import('./gamba-subscription-config.js').readSubscriptionConfig>} config
 */
function apply(config) {
  if (!config) return;

  // Una sola lectura del DOM para las dos pasadas de esta llamada: el par de
  // moneda lo pinta el servidor y no cambia en toda la vida de la página.
  const money = moneyContext();

  for (const node of document.querySelectorAll('[data-gamba-sus-subtitle-tpl]')) {
    paint(node, node.dataset.gambaSusSubtitleTpl, config, money);
  }

  for (const card of document.querySelectorAll('[data-gamba-sus-title-tpl], [data-gamba-sus-text-tpl]')) {
    paint(card.querySelector('.gamba-benefit-card__title'), card.dataset.gambaSusTitleTpl, config, money);
    paint(card.querySelector('.gamba-benefit-card__text'), card.dataset.gambaSusTextTpl, config, money);
  }

  applyFrequencies(config);
}

/**
 * Esconde las cards de frecuencia que el endpoint no habilita.
 *
 * LO QUE ESTA FUNCIÓN NO PUEDE HACER, y es el pendiente que la medición deja
 * abierto (sección 13, punto 7): si la app habilita un `count` que no tiene card
 * cargada, la frecuencia no aparece. La ilustración y el copy de una frecuencia
 * son contenido, no datos, y no se pueden inventar desde acá.
 *
 * Y LA GUARDA QUE EVITA LA PANTALLA VACÍA: si el endpoint devuelve una lista de
 * frecuencias que no coincide con NINGUNA card cargada, no se esconde nada. Una
 * sección de frecuencias sin una sola card visible es peor que una card de más:
 * la primera parece un theme roto, la segunda un dato viejo.
 *
 * @param {{ frequencies: ReadonlyArray<{ count: number }> }} config
 */
function applyFrequencies(config) {
  const cards = [...document.querySelectorAll('[data-gamba-sus-frequency]')];
  if (cards.length === 0) return;

  const enabled = new Set((config.frequencies ?? []).map((frequency) => frequency.count));
  if (enabled.size === 0) return;

  const matches = cards.filter((card) => enabled.has(Number(card.dataset.gambaSusFrequency)));
  if (matches.length === 0) return;

  for (const card of cards) {
    // `hidden` y no `style.display`: es el estado, no la presentación, y la
    // hoja de la sección ya tiene su regla `[hidden]` (que hace falta porque la
    // card declara su propio `display`, y eso le gana al `hidden` del UA).
    card.hidden = !enabled.has(Number(card.dataset.gambaSusFrequency));
  }
}

// Dos pasadas, y la primera es SÍNCRONA a propósito: `read()` devuelve lo mejor
// que se sabe hoy —el valor cacheado de la app, si alguna pantalla ya lo pidió—
// así que la segunda visita a la landing no pinta el fallback para reemplazarlo
// 200ms después. Ese salto de número es exactamente lo que el módulo de config
// existe para evitar.
apply(window.GambaSubscriptionConfig?.read());

// Y la segunda cuando el endpoint contesta. `load()` NUNCA rechaza (cae al
// fallback), así que no lleva `.catch()`.
window.GambaSubscriptionConfig?.load().then(apply);
